import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  startDateChanged,
  calculationTypeChanged,
  daysChanged,
  weeksChanged,
  monthsChanged,
  yearsChanged,
  calculateDate,
  setQuickCalculation,
  clearError,
  resetCalculator,
  formatDate,
  getDaysBetween,
  isWeekend,
  selectState,
} from "./DateCalculatorSlice";
import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  Alert,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { CustomButton } from "../../components/custom_button/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CalculateIcon from "@mui/icons-material/Calculate";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventIcon from "@mui/icons-material/Event";
import WeekendIcon from "@mui/icons-material/Weekend";
import WorkIcon from "@mui/icons-material/Work";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function DateCalculator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  const handleCalculate = useCallback(() => {
    dispatch(calculateDate());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    dispatch(resetCalculator());
  }, [dispatch]);

  const handleQuickCalculation = useCallback(
    (days: number, weeks: number, months: number, years: number) => {
      dispatch(setQuickCalculation({ days, weeks, months, years }));
    },
    [dispatch]
  );

  const quickCalculations = [
    {
      label: t("dateCalculator.quick.tomorrow"),
      days: 1,
      weeks: 0,
      months: 0,
      years: 0,
    },
    {
      label: t("dateCalculator.quick.nextWeek"),
      days: 0,
      weeks: 1,
      months: 0,
      years: 0,
    },
    {
      label: t("dateCalculator.quick.nextMonth"),
      days: 0,
      weeks: 0,
      months: 1,
      years: 0,
    },
    {
      label: t("dateCalculator.quick.nextYear"),
      days: 0,
      weeks: 0,
      months: 0,
      years: 1,
    },
    {
      label: t("dateCalculator.quick.yesterday"),
      days: 1,
      weeks: 0,
      months: 0,
      years: 0,
      subtract: true,
    },
    {
      label: t("dateCalculator.quick.lastWeek"),
      days: 0,
      weeks: 1,
      months: 0,
      years: 0,
      subtract: true,
    },
    {
      label: t("dateCalculator.quick.lastMonth"),
      days: 0,
      weeks: 0,
      months: 1,
      years: 0,
      subtract: true,
    },
    {
      label: t("dateCalculator.quick.lastYear"),
      days: 0,
      weeks: 0,
      months: 0,
      years: 1,
      subtract: true,
    },
  ];

  const hasAnyValue =
    state.days > 0 || state.weeks > 0 || state.months > 0 || state.years > 0;
  const daysBetween = state.resultDate
    ? getDaysBetween(state.startDate, state.resultDate)
    : 0;

  const renderErrorMessage = (error: string) => {
    if (error === "Please enter a valid date")
      return t("dateCalculator.errors.invalidDate");
    if (error === "Error calculating date")
      return t("dateCalculator.errors.calculationError");
    return error;
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("dateCalculator.title")}
      pageSeoTitle={t("dateCalculator.seoTitle")}
      pageDescription={t("dateCalculator.description")}
    >
      <Grid2 container spacing={3}>
        {/* Error Display */}
        {state.error && (
          <Grid2 xs={12}>
            <Alert
              severity="error"
              onClose={() => dispatch(clearError())}
              sx={{ mb: 2 }}
            >
              {renderErrorMessage(state.error)}
            </Alert>
          </Grid2>
        )}

        {/* Date Input */}
        <Grid2 xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("dateCalculator.startDate.title")}
            </Typography>

            <TextField
              fullWidth
              label={t("dateCalculator.startDate.label")}
              type="date"
              value={state.startDate}
              onChange={(e) => dispatch(startDateChanged(e.target.value))}
              InputLabelProps={{
                shrink: true,
              }}
            />

            {state.startDate && (
              <Box
                sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <EventIcon sx={{ color: "#7895a1" }} />
                <Typography variant="body2" sx={{ color: "#aedff2" }}>
                  {formatDate(state.startDate)}
                </Typography>
              </Box>
            )}

            {isWeekend(state.startDate) ? (
              <Chip
                icon={<WeekendIcon />}
                label={t("dateCalculator.weekend")}
                size="small"
                sx={{ backgroundColor: "#FF9800", color: "white", mt: 2 }}
              />
            ) : (
              <Chip
                icon={<WorkIcon />}
                label={t("dateCalculator.businessDay")}
                size="small"
                sx={{ backgroundColor: "#4CAF50", color: "white", mt: 2 }}
              />
            )}
          </Paper>
        </Grid2>

        {/* Calculation Type */}
        <Grid2 xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 3,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("dateCalculator.calculationType.title")}
            </Typography>

            <FormControl fullWidth>
              <InputLabel>
                {t("dateCalculator.calculationType.operation")}
              </InputLabel>
              <Select
                value={state.calculationType}
                label={t("dateCalculator.calculationType.operation")}
                onChange={(e) =>
                  dispatch(
                    calculationTypeChanged(e.target.value as "add" | "subtract")
                  )
                }
              >
                <MenuItem value="add">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AddIcon sx={{ color: "#4CAF50" }} />
                    <Typography>
                      {t("dateCalculator.calculationType.add")}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="subtract">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RemoveIcon sx={{ color: "#f44336" }} />
                    <Typography>
                      {t("dateCalculator.calculationType.subtract")}
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Grid2>

        {/* Time Units Input */}
        <Grid2 xs={12}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 3,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("dateCalculator.timeUnits.title")}
            </Typography>

            <Grid2 container spacing={2}>
              <Grid2 xs={6} sm={3}>
                <TextField
                  fullWidth
                  label={t("dateCalculator.timeUnits.years")}
                  type="number"
                  value={state.years}
                  onChange={(e) =>
                    dispatch(yearsChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 999 }}
                />
              </Grid2>
              <Grid2 xs={6} sm={3}>
                <TextField
                  fullWidth
                  label={t("dateCalculator.timeUnits.months")}
                  type="number"
                  value={state.months}
                  onChange={(e) =>
                    dispatch(monthsChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 999 }}
                />
              </Grid2>
              <Grid2 xs={6} sm={3}>
                <TextField
                  fullWidth
                  label={t("dateCalculator.timeUnits.weeks")}
                  type="number"
                  value={state.weeks}
                  onChange={(e) =>
                    dispatch(weeksChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 999 }}
                />
              </Grid2>
              <Grid2 xs={6} sm={3}>
                <TextField
                  fullWidth
                  label={t("dateCalculator.timeUnits.days")}
                  type="number"
                  value={state.days}
                  onChange={(e) =>
                    dispatch(daysChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 999 }}
                />
              </Grid2>
            </Grid2>
          </Paper>
        </Grid2>

        {/* Quick Calculations */}
        <Grid2 xs={12}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 3,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("dateCalculator.quick.title")}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {quickCalculations.map((calc) => (
                <Chip
                  key={calc.label as string}
                  label={calc.label}
                  onClick={() => {
                    if ((calc as any).subtract) {
                      dispatch(calculationTypeChanged("subtract"));
                    } else {
                      dispatch(calculationTypeChanged("add"));
                    }
                    handleQuickCalculation(
                      calc.days,
                      calc.weeks,
                      calc.months,
                      calc.years
                    );
                  }}
                  sx={{
                    backgroundColor: "#7895a1",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#5a7a8a",
                    },
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Grid2>

        {/* Calculate Button */}
        <Grid2 xs={12}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
          >
            <CustomButton
              icon={<CalculateIcon sx={{ color: "#FFD9A8" }} />}
              label={t("dateCalculator.actions.calculate")}
              onClick={handleCalculate}
              disabled={!hasAnyValue}
            />
            <CustomButton
              icon={<RefreshIcon sx={{ color: "#FFD9A8" }} />}
              label={t("dateCalculator.actions.reset")}
              onClick={handleReset}
            />
          </Stack>
        </Grid2>

        {/* Result */}
        {state.resultDate && (
          <Grid2 xs={12}>
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                background: "linear-gradient(135deg, #282B39 0%, #1a1d2a 100%)",
                border: "1px solid #7895a1",
                borderRadius: 3,
                mb: 3,
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, color: "#FFD9A8" }}>
                {t("dateCalculator.result.title")}
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: "2rem", sm: "3rem" },
                  fontWeight: 300,
                  color: "#FFD9A8",
                  mb: 2,
                  fontFamily: "monospace",
                }}
              >
                {state.resultDate}
              </Typography>

              <Typography variant="h6" sx={{ mb: 2, color: "#aedff2" }}>
                {formatDate(state.resultDate)}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                {isWeekend(state.resultDate) ? (
                  <Chip
                    icon={<WeekendIcon />}
                    label={t("dateCalculator.weekend")}
                    sx={{ backgroundColor: "#FF9800", color: "white" }}
                  />
                ) : (
                  <Chip
                    icon={<WorkIcon />}
                    label={t("dateCalculator.businessDay")}
                    sx={{ backgroundColor: "#4CAF50", color: "white" }}
                  />
                )}
              </Box>

              <Typography variant="body1" sx={{ color: "#aedff2" }}>
                {t("dateCalculator.result.daysBetween", { count: daysBetween })}
              </Typography>
            </Paper>
          </Grid2>
        )}

        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>

        {/* SEO Content */}
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("dateCalculator.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("dateCalculator.content.freeDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("dateCalculator.content.easyHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("dateCalculator.content.easyDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("dateCalculator.content.planningHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("dateCalculator.content.planningDescription")}
          </Typography>
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
